import { template as _$template } from "solid-js/web";
import { insert as _$insert } from "solid-js/web";
import { setAttribute as _$setAttribute } from "solid-js/web";
import { createComponent as _$createComponent } from "solid-js/web";
var _tmpl$ = /*#__PURE__*/_$template(`<main class=duel><img class=duel__backdrop alt aria-hidden=true>`);
import { A, useNavigate, useSearchParams } from "@solidjs/router";
import { createSignal, onCleanup, onMount, Show } from "solid-js";
import { isRoomCode, makeRoomCode, normalizeRoomCode, signalUrl } from "@red-opus/net";
import { browserSettingsStore } from "../audio/settings.ts";
import { DuelFlight } from "../net/DuelFlight.tsx";
import { DuelLobby } from "../net/DuelLobby.tsx";
import { createDuelConnection } from "../net/duel-connection.ts";
import { canRematch } from "../net/duel-outcome.ts";
import { DEFAULT_DUEL_TIER, cryptoRandom, duelRoomLink, duelRoomPath, newDuelSeed, pageOrigin, parseDuelRoom } from "../net/duel-setup.ts";
import { EMPTY_TURN, iceConfigFrom, loadTurnConfig, saveTurnConfig } from "../net/ice-settings.ts";
import { TITLE_BACKDROP_PATH } from "../title-menu.ts";

/** How often the route re-reads the connection, so a stall or a verdict shows up promptly. */
const POLL_MS = 120;
const DISCONNECTED = {
  phase: "idle",
  role: null,
  room: "",
  outcome: "flying",
  opponentAircraft: null,
  waiting: false,
  build: "unconfirmed",
  error: null
};

/**
 * `/duel` — AD-008's multiplayer surface, and the whole of it: a room code, an
 * aeroplane each, and then the flight scene mounted on a lockstep runner.
 *
 * `/duel?room=CODE` is the shared link, and it joins on arrival — the code in
 * the URL is the entire invitation, and there is nothing else to configure
 * before the two of you are connected.
 */
export function Duel() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const store = browserSettingsStore();
  const [roomInput, setRoomInput] = createSignal(normalizeRoomCode(parseDuelRoom(searchParams) ?? ""));
  const [aircraft, setAircraft] = createSignal("CAMEL");
  const [tier, setTier] = createSignal(DEFAULT_DUEL_TIER);
  const [turn, setTurn] = createSignal(loadTurnConfig(store) ?? EMPTY_TURN);
  const [ice, setIce] = createSignal(iceConfigFrom(loadTurnConfig(store)));
  const [connection, setConnection] = createSignal(undefined);
  const [status, setStatus] = createSignal(DISCONNECTED);
  /** Bumped by every refresh so the runner accessor below re-reads. */
  const [beat, setBeat] = createSignal(0);
  function refresh() {
    setStatus(connection()?.status() ?? DISCONNECTED);
    setBeat(count => count + 1);
  }
  const runner = () => {
    beat();
    return connection()?.runner() ?? undefined;
  };
  function enter(code) {
    if (!isRoomCode(code) || connection()) return;
    setConnection(createDuelConnection({
      room: code,
      signalUrl: signalUrl(pageOrigin(), code),
      ice: ice(),
      aircraft: aircraft(),
      onChange: refresh,
      newSeed: newDuelSeed
    }));
    setRoomInput(code);
    navigate(duelRoomPath(code), {
      replace: true
    });
    refresh();
  }
  function leave() {
    connection()?.leave();
    setConnection(undefined);
    setStatus(DISCONNECTED);
    navigate("/duel", {
      replace: true
    });
  }
  function choose(next) {
    setAircraft(next);
    connection()?.choose(next);
  }
  function persistTurn() {
    const saved = saveTurnConfig(store, turn());
    setIce(iceConfigFrom(saved));
    setTurn(saved ?? EMPTY_TURN);
  }
  onMount(() => {
    const joining = normalizeRoomCode(parseDuelRoom(searchParams) ?? "");
    if (isRoomCode(joining)) enter(joining);
    const timer = setInterval(refresh, POLL_MS);
    onCleanup(() => {
      clearInterval(timer);
      connection()?.leave();
    });
  });
  const rematchable = () => status().role === "host" && canRematch(status().outcome);
  const rematchHint = () => status().role === "host" ? "This duel cannot be restarted." : "The host starts the rematch.";
  return _$createComponent(Show, {
    get when() {
      return runner();
    },
    keyed: true,
    get fallback() {
      return (() => {
        var _el$ = _tmpl$(),
          _el$2 = _el$.firstChild;
        _$setAttribute(_el$2, "src", TITLE_BACKDROP_PATH);
        _$insert(_el$, _$createComponent(A, {
          "class": "duel__back",
          href: "/",
          children: "← Title"
        }), null);
        _$insert(_el$, _$createComponent(DuelLobby, {
          get status() {
            return status();
          },
          get connected() {
            return connection() !== undefined;
          },
          get roomInput() {
            return roomInput();
          },
          onRoomInput: value => setRoomInput(normalizeRoomCode(value)),
          onCreate: () => enter(makeRoomCode(cryptoRandom)),
          onJoin: () => enter(normalizeRoomCode(roomInput())),
          onLeave: leave,
          get aircraft() {
            return aircraft();
          },
          onChooseAircraft: choose,
          get tier() {
            return tier();
          },
          onChooseTier: setTier,
          onLaunch: () => connection()?.launch(tier()),
          get shareLink() {
            return duelRoomLink(pageOrigin(), status().room || roomInput());
          },
          get ice() {
            return ice();
          },
          get turn() {
            return turn();
          },
          onChangeTurn: setTurn,
          onSaveTurn: persistTurn
        }), null);
        return _el$;
      })();
    },
    children: live => _$createComponent(DuelFlight, {
      runner: live,
      get outcome() {
        return status().outcome;
      },
      get waiting() {
        return status().waiting;
      },
      get canRematch() {
        return rematchable();
      },
      get rematchHint() {
        return rematchHint();
      },
      onRematch: () => connection()?.rematch(tier()),
      onExit: leave
    })
  });
}